import { User } from "oidc-client-ts";
import { store } from "store";
import { SSO_SESSION_EXPIRED } from "reducers/ssoSessionExpired";
import TokenManager from "utils/TokenManager";

/**
 * Keeps Ping SSO tokens in sync across browser tabs without ever persisting
 * them to localStorage. oidc-client-ts's userStore stays on sessionStorage
 * (per tab); tokens are relayed between tabs in-memory via BroadcastChannel.
 *
 * Only the elected "leader" tab is allowed to call signinSilent(). Ping issues
 * a new refresh_token on every use and revokes the previous one, so if every
 * tab refreshed independently they would race and invalidate each other's
 * refresh_token. Electing a single leader (localStorage only stores a small
 * non-sensitive lock record, never tokens) avoids that race entirely.
 */
const LOCK_KEY = "ssoAuthLeaderLock";
const LOCK_TTL = 7000;
const HEARTBEAT_INTERVAL = 2500;
const CHANNEL_NAME = "sso-auth-sync";

const createTabId = () =>
  `tab_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

class SsoTabSync {
  constructor() {
    this.tabId = createTabId();
    this.isLeader = false;
    this.userManager = null;
    this.channel = null;
    this.heartbeatTimer = null;
    this.monitorTimer = null;
  }

  start(userManager) {
    if (this.userManager) return; // already started
    this.userManager = userManager;

    if (typeof BroadcastChannel !== "undefined") {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      this.channel.onmessage = (event) => this._handleMessage(event.data);
    }

    userManager.events.addAccessTokenExpiring(() =>
      this._onAccessTokenExpiring()
    );
    userManager.events.addUserSignedOut(() => this.broadcastLogout());

    this._tryAcquireLeader();
    this.monitorTimer = setInterval(() => this._monitor(), 1000);
    // Covers tabs opened after another tab already has a session
    this._requestUser();
  }

  // --- leader election: localStorage only ever holds lock metadata, never tokens ---
  _tryAcquireLeader() {
    try {
      const now = Date.now();
      const currentLock = JSON.parse(localStorage.getItem(LOCK_KEY) || "{}");
      if (
        currentLock.ownerTabId &&
        currentLock.expiresAt > now &&
        currentLock.ownerTabId !== this.tabId
      ) {
        this.isLeader = false;
        return;
      }
      localStorage.setItem(
        LOCK_KEY,
        JSON.stringify({ ownerTabId: this.tabId, expiresAt: now + LOCK_TTL })
      );
      this.isLeader = true;
      if (!this.heartbeatTimer) {
        this.heartbeatTimer = setInterval(
          () => this._renewLock(),
          HEARTBEAT_INTERVAL
        );
      }
    } catch (error) {
      // localStorage unavailable — act as leader for this tab only
      this.isLeader = true;
    }
  }

  _renewLock() {
    try {
      const now = Date.now();
      const lock = JSON.parse(localStorage.getItem(LOCK_KEY) || "{}");
      if (lock.ownerTabId !== this.tabId) {
        this.isLeader = false;
        return;
      }
      localStorage.setItem(
        LOCK_KEY,
        JSON.stringify({ ownerTabId: this.tabId, expiresAt: now + LOCK_TTL })
      );
    } catch (error) {
      // ignore
    }
  }

  _monitor() {
    try {
      const lock = JSON.parse(localStorage.getItem(LOCK_KEY) || "{}");
      if (!lock.ownerTabId || lock.expiresAt <= Date.now()) {
        this._tryAcquireLeader();
      }
    } catch (error) {
      // ignore
    }
  }

  // --- refresh: leader-only, avoids racing Ping's refresh-token rotation ---
  async _onAccessTokenExpiring() {
    if (!this.isLeader) return;
    try {
      const user = await this.userManager.signinSilent();
      if (user) this.broadcastUser(user);
    } catch (error) {
      console.error("Ping SSO silent renew failed:", error);
    }
  }

  // --- cross-tab relay: in-memory BroadcastChannel only, never localStorage ---
  broadcastUser(user) {
    if (!this.channel) return;
    this.channel.postMessage({
      type: "user-updated",
      tabId: this.tabId,
      user: user.toStorageString(),
    });
  }

  broadcastLogout() {
    if (!this.channel) return;
    this.channel.postMessage({ type: "logout", tabId: this.tabId });
  }

  _requestUser() {
    if (!this.channel) return;
    this.channel.postMessage({ type: "request-user", tabId: this.tabId });
  }

  async _handleMessage(data) {
    if (!data || data.tabId === this.tabId) return;
    if (data.type === "user-updated" && data.user) {
      try {
        await this.userManager.storeUser(User.fromStorageString(data.user));
        // A tab parked on /login while waiting for sign-in elsewhere can now return
        const oldUrl = window.sessionStorage.getItem("oldUrl");
        if (window.location.pathname === "/login" && oldUrl) {
          window.sessionStorage.removeItem("oldUrl");
          window.open(oldUrl, "_self");
        }
      } catch (error) {
        console.error("Failed to apply synced SSO user:", error);
      }
    } else if (data.type === "request-user") {
      const currentUser = await this.userManager.getUser();
      if (currentUser) this.broadcastUser(currentUser);
    } else if (data.type === "logout") {
      await this.userManager.removeUser();
      // Show the session-expired modal in this tab too, instead of a hard
      // redirect that would bypass it for SSO/Ping users
      store.dispatch({
        type: SSO_SESSION_EXPIRED,
        payload: { userEmail: TokenManager.getUserEmail() },
      });
    }
  }
}

export const ssoTabSync = new SsoTabSync();
